import { type ApiFetchContext, apiFetch } from "../client";
import type { DivisionOption, DivisionRaw } from "../types/division";

/**
 * Pick a display label for a division row. The backend duplicates the same
 * string into nameLocal/nameEn/nameCn for w_state/w_city, but other division
 * sources may differ, so prefer local (Russian) -> English -> Chinese.
 */
function pickName(raw: DivisionRaw): string {
  return (raw.nameLocal || raw.nameEn || raw.nameCn || "").trim();
}

export function normalizeDivision(raw: DivisionRaw): DivisionOption {
  // State rows alias `id as pid` (id is null); city rows use `id`.
  const id = (raw.id ?? raw.pid ?? "").toString();
  return {
    id,
    name: pickName(raw),
    postalCode: raw.postalCode ? raw.postalCode.toString() : null,
  };
}

/**
 * List the provinces/states of a country.
 * @param countryCode ISO country code, e.g. "RU".
 */
export async function listDivisionStates(
  ctx: ApiFetchContext,
  countryCode: string,
): Promise<DivisionOption[]> {
  const raw = await apiFetch<DivisionRaw[]>(
    ctx,
    `/base/division/state?pid=${encodeURIComponent(countryCode)}`,
  );
  return (raw ?? []).map(normalizeDivision).filter((d) => d.id.length > 0);
}

/**
 * List the cities of a state/province.
 * @param stateId the `id` of a state returned by {@link listDivisionStates}.
 */
export async function listDivisionCities(
  ctx: ApiFetchContext,
  stateId: string,
): Promise<DivisionOption[]> {
  const raw = await apiFetch<DivisionRaw[]>(
    ctx,
    `/base/division/city?pid=${encodeURIComponent(stateId)}`,
  );
  return (raw ?? []).map(normalizeDivision).filter((d) => d.id.length > 0);
}
